import React, { useState, useRef, useEffect, useMemo } from 'react';
import { NavyBg, PAGE_ROOT, OR } from '../../config/styles';

/* ── يفصل الرقم عن البادئة/اللاحقة: "+200" → {prefix:"+", target:200, suffix:""} ── */
function parseStatValue(raw) {
  const str = String(raw ?? '');
  const m = str.match(/-?\d[\d,]*\.?\d*/);
  if (!m) return { numeric: false, raw: str };
  const numStr = m[0].replace(/,/g, '');
  const target = parseFloat(numStr);
  if (!isFinite(target)) return { numeric: false, raw: str };
  const decimals = (numStr.split('.')[1] || '').length;
  return {
    numeric: true,
    target,
    decimals,
    hadComma: m[0].includes(','),
    prefix: str.slice(0, m.index),
    suffix: str.slice(m.index + m[0].length),
  };
}

function formatNum(n, decimals, withComma) {
  const fixed = Number(n).toFixed(decimals);
  if (!withComma) return fixed;
  const [int, dec] = fixed.split('.');
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return dec ? `${grouped}.${dec}` : grouped;
}

/* ── رقم بيعدّ من صفر للهدف لما run=true (easeOutCubic عشان يحس إنه بيعدّ بجد) ── */
function StatNumber({ value, run }) {
  const info = useMemo(() => parseStatValue(value), [value]);
  const [disp, setDisp] = useState(info.numeric ? 0 : info.raw);

  useEffect(() => {
    if (!info.numeric) { setDisp(info.raw); return; }
    if (!run) { setDisp(0); return; }

    const reduce = typeof window !== 'undefined'
      && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) { setDisp(info.target); return; }

    let raf;
    const dur = 1500;
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisp(info.target * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
      else setDisp(info.target);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [run, info]);

  if (!info.numeric) return <>{info.raw}</>;
  return <>{info.prefix}{formatNum(disp, info.decimals, info.hadComma)}{info.suffix}</>;
}

export default function AboutSection({ settings, lang, t, onSetPage }) {
  const ar = lang === 'ar';
  const enabled = settings?.about_enabled !== false;
  const title   = ar ? (settings?.about_title_ar || 'من نحن') : (settings?.about_title_en || 'About Us');
  const text    = ar ? (settings?.about_text_ar  || '') : (settings?.about_text_en || '');
  const stats   = (() => {
    try {
      const s = settings?.about_stats;
      return Array.isArray(s) ? s : (typeof s === 'string' ? JSON.parse(s) : []);
    } catch { return []; }
  })();

  const [hoveredStat, setHoveredStat] = useState(null);
  const standalone = typeof onSetPage === 'function';

  /* ── يبدأ العدّ: فورًا لو السيكشن ظاهر، وإلا لما تنزّل له (مرة واحدة) ── */
  const cardRef = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = cardRef.current;
    if (!el) { setInView(true); return; }

    // لو الكارت ظاهر في الشاشة دلوقتي → ابدأ العدّ على طول
    const visibleNow = () => {
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const vw = window.innerWidth || document.documentElement.clientWidth;
      return r.top < vh && r.bottom > 0 && r.left < vw && r.right > 0;
    };
    if (visibleNow()) { setInView(true); return; }

    // وإلا راقب لحد ما يدخل الشاشة بالـ scroll
    if (typeof IntersectionObserver === 'undefined') { setInView(true); return; }
    const ob = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { setInView(true); ob.disconnect(); } });
    }, { threshold: 0.2 });
    ob.observe(el);
    return () => ob.disconnect();
  }, []);

  if (!enabled) return standalone ? (
    <div style={PAGE_ROOT}>
      <NavyBg />
      <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.8)', padding: '80px 20px' }}>—</div>
    </div>
  ) : null;

  const Card = (
    <div ref={cardRef} style={{
      background: 'rgba(255,255,255,0.05)',
      border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: '24px', padding: '38px 28px',
      boxShadow: '0 16px 44px rgba(0,0,0,0.3)',
      maxWidth: '820px', margin: standalone ? '0 auto' : '34px auto 0',
      direction: ar ? 'rtl' : 'ltr',
      fontFamily: "'Tajawal', sans-serif",
      animation: 'cardRise .6s cubic-bezier(.22,1,.36,1) both',
      position: 'relative', overflow: 'hidden',
    }}>
      {/* عنوان */}
      <div style={{ textAlign: 'center', marginBottom: '22px' }}>
        <h3 style={{
          fontSize: '26px', fontWeight: 'bold', margin: '0 0 10px',
          background: 'linear-gradient(135deg,#f97316,#fb923c)',
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
        }}>
          ✨ {title}
        </h3>
        <div style={{ width: '60px', height: '3px', background: 'linear-gradient(135deg,#f97316,#fb923c)', borderRadius: '2px', margin: '0 auto' }} />
      </div>

      {/* النص */}
      {text && (
        <p style={{
          fontSize: '15px', color: 'rgba(255,255,255,0.78)', lineHeight: '2',
          textAlign: 'center', maxWidth: '620px', margin: '0 auto 28px', whiteSpace: 'pre-line',
        }}>
          {text}
        </p>
      )}

      {/* إحصاءات قابلة للتعديل — بعدّاد متحرك */}
      {stats.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', flexWrap: 'wrap' }}>
          {stats.map((stat, i) => {
            const on = hoveredStat === i;
            return (
              <div key={i}
                onMouseEnter={() => setHoveredStat(i)}
                onMouseLeave={() => setHoveredStat(null)}
                style={{
                  background: on ? 'linear-gradient(135deg,#f97316,#fb923c)' : 'rgba(255,255,255,0.04)',
                  borderRadius: '16px', padding: '20px 28px', textAlign: 'center', minWidth: '120px',
                  border: `1px solid ${on ? 'transparent' : 'rgba(255,255,255,0.1)'}`,
                  transition: 'all 0.3s cubic-bezier(.34,1.56,.64,1)',
                  transform: on ? 'translateY(-5px) scale(1.05)' : 'translateY(0) scale(1)',
                  boxShadow: on ? '0 14px 30px rgba(249,115,22,0.4)' : 'none', cursor: 'default',
                }}>
                <div style={{
                  fontSize: '32px', fontWeight: 'bold',
                  color: on ? '#fff' : 'transparent',
                  background: on ? 'none' : 'linear-gradient(135deg,#f97316,#fb923c)',
                  WebkitBackgroundClip: on ? 'unset' : 'text',
                  WebkitTextFillColor: on ? '#fff' : 'transparent',
                  fontVariantNumeric: 'tabular-nums',
                }}>
                  <StatNumber value={stat.value} run={inView} />
                </div>
                <div style={{ fontSize: '12.5px', marginTop: '6px', fontWeight: '600', color: on ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.6)' }}>
                  {ar ? stat.label_ar : (stat.label_en || stat.label_ar)}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  // صفحة مستقلة → نغلّفها بخلفية الأكاديمية + زر رجوع
  if (standalone) {
    return (
      <div style={PAGE_ROOT}>
        <NavyBg />
        {Card}
        <div style={{ textAlign: 'center', marginTop: '28px' }}>
          <button onClick={() => onSetPage('home')}
            style={{ padding: '11px 28px', background: 'rgba(255,255,255,0.95)', color: OR, border: 'none', borderRadius: '10px', fontSize: '14px', cursor: 'pointer', fontWeight: 'bold', boxShadow: '0 4px 16px rgba(0,0,0,0.18)' }}>
            {t?.backHome || (ar ? 'العودة للرئيسية' : 'Back to Home')}
          </button>
        </div>
      </div>
    );
  }

  // مدمجة جوّه الهوم
  return Card;
}
